import React, { useEffect, useReducer } from 'react';
import cx from 'classnames';
import {
  getProjectRenderingStatus,
  getPublishedProjectMediaImageUuid,
  requestPublishedProjectRendering,
} from 'cards/util/digitalSaveTheDate';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { getImageUrl } from 'paper/utils/helpers';
import { isMobile } from '@zola-helpers/client/dist/es/util/responsive';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import DigitalOnCopyTemplate from './DigitalOnCopyTemplate';
import { trackEvent } from '../../../../../util/trackingHelper';
import styles from './styles.module.less';

const FIVE_MINUTE_TIMEOUT = 60000;
const PROCESSED_SUCCESS = 'PROCESSED_SUCCESS';
const PROCESSED_FAILED = 'PROCESSED_FAILED';

const DEFAULT_STATE = {
  hasDownloaded: false,
  mediaFileUrl: null,
  isReady: false,
  timedOut: false,
  step: 1,
  isChecked: false,
  percentComplete: 0,
};

function reducer(state: any, action: any) {
  switch (action.type) {
    case 'update_percent':
      return {
        ...state,
        percentComplete: action.payload,
        isReady: action.payload === 100,
      };
    case 'timed_out':
      return {
        ...state,
        timedOut: true,
      };
    case 'retry':
      return {
        ...state,
        percentComplete: 0,
        timedOut: false,
        step: 1,
      };
    case 'step2':
      return {
        ...state,
        step: 2,
      };
    default:
      throw new Error();
  }
}

type DigitalGeneratingTemplateProps = {
  projectUUID: string;
  setDstdImageUrl: (imgUrl: string) => void;
};

const DigitalGeneratingTemplate = ({
  projectUUID,
  setDstdImageUrl,
}: DigitalGeneratingTemplateProps): JSX.Element => {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);
  const { timedOut, step, percentComplete } = state;
  let interval: any;

  const onReceiveImage = () => {
    getPublishedProjectMediaImageUuid({ projectUUID })
      .then(response => {
        setDstdImageUrl(getImageUrl(response.image_id));
        dispatch({ type: 'update_percent', payload: 100 });
        clearInterval(interval);
      })
      .catch(err => console.log(err));
  };

  const imageRenderLoop = () => {
    requestPublishedProjectRendering({ projectUUID })
      .then(imageStatus => {
        let percentage = 0;
        const {
          async_request_status: requestStatus,
          async_request_uuid: requestUuid,
        } = imageStatus;
        if (requestStatus !== PROCESSED_SUCCESS) {
          const randomNum = Math.floor(Math.random() * Math.floor(8));
          dispatch({ type: 'update_percent', payload: percentage + randomNum });
          percentage += randomNum;
          // set timer to re-fetch the image render every 5 seconds
          interval = setInterval(() => {
            if (percentage < 90) {
              const randomNumber = Math.floor(Math.random() * Math.floor(8));
              dispatch({ type: 'update_percent', payload: percentage + randomNumber });
              percentage += randomNumber;
            }
            getProjectRenderingStatus(requestUuid)
              .then((res: any) => {
                if (res.async_request_status === PROCESSED_SUCCESS) {
                  percentage = 100;
                  onReceiveImage();
                } else if (res.async_request_status === PROCESSED_FAILED) {
                  dispatch({ type: 'update_percent', payload: 0 });
                  percentage = 0;
                  clearInterval(interval);
                }
              })
              .catch(err => console.log(err));
          }, 5000);
        } else {
          percentage = 100;
          onReceiveImage();
        }

        // timeout after 5 minutes
        setTimeout(() => {
          clearInterval(interval);
          dispatch({ type: 'timed_out' });
        }, FIVE_MINUTE_TIMEOUT);
      })
      .catch(() => {});
  };

  useEffect(() => {
    imageRenderLoop();
    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      {step === 1 ? (
        <div className={styles.container}>
          <div className={cx(styles.stepOneWrapper, {})}>
            <span className={styles.stepOneTitle}>
              {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
              Working some magic <span role="img">🔮</span>
            </span>
            <span className={styles.stepOneSubTitle}>This could take a few seconds.</span>
            <div className={styles.progressWrapper}>
              <div className={styles.infoWrapper}>
                <div className={styles.progressInfo}>
                  <span className={styles.progressTitle}>Generating your email</span>
                  <span className={styles.progressPercentage}>{`${percentComplete}%`}</span>
                </div>
                <div className={styles.progressBar}>
                  <div style={{ width: `${percentComplete}%` }} className={styles.progressInner} />
                </div>
              </div>
            </div>
            <div className={styles.copyEmailTemplateWWrapper}>
              <ButtonV2
                id="copy-dstd-email-template"
                disabled={percentComplete !== 100}
                onClick={() => {
                  trackEvent('CTA Clicked', {
                    business_unit: BUSINESS_UNIT.DIGITAL_SAVE_THE_DATE,
                    cta_type: 'BUTTON',
                    cta: 'COPY_EMAIL_TEMPLATE_MODAL',
                  });
                  dispatch({ type: 'step2' });
                }}
                fullWidth={isMobile()}
              >
                Copy Your Email Template
              </ButtonV2>
            </div>
          </div>
        </div>
      ) : null}
      {step === 2 ? <DigitalOnCopyTemplate /> : null}
      {timedOut && percentComplete !== 100 && (
        <div className={styles.container}>
          <span className={styles.stepOneTitle}>Render has timed out</span>
        </div>
      )}
    </>
  );
};

export default DigitalGeneratingTemplate;
